/**
 * Очередь синхронизации подходов.
 *
 * Запись подхода никогда не ждёт сеть: она мгновенно ложится в IndexedDB и
 * возвращает управление интерфейсу. Отправкой занимается фоновый прогон.
 * Это и есть то, что делает «логирование в 2 тапа» (продуктовый принцип №2)
 * честным обещанием, а не обещанием при хорошем интернете.
 *
 * Разница между «сеть не дошла» и «сервер отказал» здесь принципиальная:
 * первое — повторяем вечно, второе — помечаем как сбой и показываем человеку.
 * Бесконечный повтор запроса, который сервер уже отверг по существу
 * (например, RPE 11), не починится никогда и только сожжёт батарею.
 */

import { ApiError, NetworkError, api } from '../api/client'
import type { SetInput, WorkoutSet } from '../api/types'
import { dequeue, enqueue, queuedItems, type QueuedSet } from './db'

type Listener = (state: QueueState) => void

export interface QueueState {
  pending: number
  failed: number
  syncing: boolean
  online: boolean
}

const listeners = new Set<Listener>()
let syncing = false

function newUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // Древние webview без randomUUID: важна не криптостойкость, а уникальность
  // в пределах одного устройства.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  void notify()
  return () => listeners.delete(listener)
}

async function notify(): Promise<void> {
  const items = await queuedItems()
  const state: QueueState = {
    pending: items.filter((i) => i.status === 'pending').length,
    failed: items.filter((i) => i.status === 'failed').length,
    syncing,
    online: navigator.onLine,
  }
  listeners.forEach((l) => l(state))
}

/**
 * Записать подход. Возвращается сразу, до похода в сеть.
 *
 * `client_uuid` генерируется здесь и остаётся неизменным при всех повторах —
 * на нём держится идемпотентность на стороне сервера.
 */
export async function recordSet(payload: SetInput): Promise<QueuedSet> {
  const uuid = payload.client_uuid ?? newUuid()
  const item: QueuedSet = {
    uuid,
    payload: { ...payload, client_uuid: uuid },
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
  }
  await enqueue(item)
  void notify()
  // Отправку не ждём: интерфейс уже нарисовал подход как записанный.
  void flush()
  return item
}

/**
 * Прогнать очередь. Безопасно звать сколько угодно раз: повторный вызов
 * во время работы просто выходит.
 */
export async function flush(): Promise<void> {
  if (syncing) return
  syncing = true
  void notify()

  try {
    for (const item of await queuedItems()) {
      if (item.status !== 'pending') continue
      try {
        await api.post<WorkoutSet>('/sets', item.payload)
        await dequeue(item.uuid)
      } catch (error) {
        if (error instanceof NetworkError) {
          // Связи нет — оставляем в очереди и прекращаем прогон: остальные
          // элементы упрутся в ту же стену, незачем их трогать.
          await enqueue({ ...item, attempts: item.attempts + 1 })
          break
        }
        if (error instanceof ApiError && error.isAuth) {
          // Сессия мертва. Данные не теряем: после входа прогон повторится.
          break
        }
        if (error instanceof ApiError && error.status >= 500) {
          // Сбой НА СТОРОНЕ СЕРВЕРА, а не отказ по существу: рестарт при
          // деплое, 502 от nginx, упавший запрос. Пометить такой подход
          // «не принят» значит объявить факт спорным из-за чужой аварии и
          // потребовать от человека ручного повтора. Оставляем в очереди и
          // прекращаем прогон — остальные упрутся в ту же стену.
          await enqueue({ ...item, attempts: item.attempts + 1 })
          break
        }
        if (error instanceof ApiError) {
          await enqueue({
            ...item,
            attempts: item.attempts + 1,
            status: 'failed',
            lastError: error.message,
          })
          continue
        }
        throw error
      }
    }
  } finally {
    syncing = false
    void notify()
  }
}

/** Повторить отправку записей, помеченных как сбойные. */
export async function retryFailed(): Promise<void> {
  for (const item of await queuedItems()) {
    if (item.status === 'failed') {
      await enqueue({ ...item, status: 'pending', lastError: undefined })
    }
  }
  await flush()
}

/** Выбросить сбойную запись — единственный способ убрать её из очереди. */
export async function discard(uuid: string): Promise<void> {
  await dequeue(uuid)
  void notify()
}

/**
 * Повесить прогон на события, после которых связь обычно появляется.
 *
 * `visibilitychange` важнее, чем кажется: телефон в кармане между подходами
 * усыпляет вкладку, и `online` может не прийти вовсе.
 */
export function startAutoSync(): () => void {
  const run = () => void flush()

  window.addEventListener('online', run)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') run()
  })
  const timer = window.setInterval(run, 30_000)
  run()

  return () => {
    window.removeEventListener('online', run)
    window.clearInterval(timer)
  }
}
